import { Progress, Text, Spinner, Icon } from '@flatfile/baseline'
import { format, lastDayOfMonth } from 'date-fns'
import { ReactNode } from 'react'
import { Product } from '../../../api/types'
import { BillingSection } from './BillingComponents'
import { displayPriceFormatted } from '../utilities/billingHelpers'
import { calculateFilesDollarAmount, calculatePdvDollarAmount, formatUsageNumbers } from '../utilities/stripe'
import { fromMaybe } from '../utilities/fromMaybe'

export type UsageTabs = 'files' | 'projects'

export const getDaysRemainingString = (currentDate: Date) => {
  const today = currentDate
  const month = format(today, 'MMMM')
  const day = format(today, 'd')
  const lastDay = format(lastDayOfMonth(today), 'd')
  // eslint-disable-next-line radix
  const daysRemaining = parseInt(lastDay) - parseInt(day)

  return `${month} ${day} (${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} remaining this month)`
}

const UsageAlert = ({ children }: { children: ReactNode }) => {
  return (
    <div className='flex items-start gap-2 mt-4'>
      <Icon name='triangle-exclamation' size={16} />
      <Text className='text-sm font-semibold'>{children}</Text>
    </div>
  )
}

const UsageCard = ({ children }: { children: ReactNode }) => (
  <div className='flex flex-col p-4 border rounded-md'>{children}</div>
)

const UsageTitleSection = ({ children }: { children: ReactNode }) => (
  <div className='flex items-center justify-between'>{children}</div>
)

const UsageTitle = ({ children }: { children: ReactNode }) => <Text className='text-lg font-semibold'>{children}</Text>

const UsageDollarAmount = ({ children }: { children: ReactNode }) => <Text className='font-bold'>{children}</Text>

const ProgressSection = ({ children }: { children: ReactNode }) => (
  <div className='flex flex-col gap-2 mt-4'>{children}</div>
)

const ProgressMeter = ({ value }: { value: number }) => <Progress value={value} className='bg-gray-200' />

const UsageAmount = ({ amount, limit }: { amount: number | string; limit: number | string }) => (
  <div className='mt-2'>
    <Text className='text-xl font-bold'>
      {amount} / <span>{limit}</span>
    </Text>
  </div>
)

const FilesUsage = ({
  filesUsage,
  pdvUsage,
  freeFileLimit,
  freePdvLimit,
  filesMeteredProduct,
  pdvMeteredProduct,
  hasSubscription,
  isLoading,
}: {
  filesUsage: number | undefined
  pdvUsage: number | undefined
  freeFileLimit: number
  freePdvLimit: number
  filesMeteredProduct: Product
  pdvMeteredProduct: Product
  hasSubscription: boolean
  isLoading: boolean
}) => {
  // if there is an existing subscription, the paid limit is inclusive of the free limits
  const getPaidLimit = (product: Product) => product.defaultPrice?.tiers?.[0]?.upTo

  const paidFileLimit = getPaidLimit(filesMeteredProduct)
  const paidPDVLimit = getPaidLimit(pdvMeteredProduct)

  const totalUsageFileLimit = paidFileLimit ?? freeFileLimit
  const totalUsagePDVLimit = paidPDVLimit ? paidPDVLimit * 1_000_000 : freePdvLimit

  const [pdvUsageDisplay, pdvLimitDisplay] = formatUsageNumbers([pdvUsage as number, totalUsagePDVLimit as number])

  const files = Math.round((fromMaybe(filesUsage, 0) / totalUsageFileLimit) * 100)
  const pdv = Math.round((fromMaybe(pdvUsage, 0) / totalUsagePDVLimit) * 100)

  const pdvDollarAmount = displayPriceFormatted(calculatePdvDollarAmount(pdvUsage!, pdvMeteredProduct), 2)
  const filesDollarAmount = displayPriceFormatted(calculateFilesDollarAmount(filesUsage!, filesMeteredProduct), 2)

  const currentDate = new Date()

  if (isLoading)
    return (
      <div className='flex justify-center'>
        <Spinner />
      </div>
    )
  return (
    <div className='flex flex-col gap-4 mt-2'>
      <Text className='font-bold'>{getDaysRemainingString(currentDate)}</Text>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <UsageCard>
          <UsageTitleSection>
            <UsageTitle>Files</UsageTitle>
            <UsageDollarAmount>{filesDollarAmount}</UsageDollarAmount>
          </UsageTitleSection>
          <ProgressSection>
            <ProgressMeter value={Math.min(100, files)} />
          </ProgressSection>
          <UsageAmount amount={filesUsage ?? 0} limit={totalUsageFileLimit} />
          {!hasSubscription && (
            <UsageAlert>Upgrade to a pay-as-you-go plan to increase your usage limit for $2.00/file.</UsageAlert>
          )}
        </UsageCard>
        <UsageCard>
          <UsageTitleSection>
            <UsageTitle>Processed Data Values (PDV)</UsageTitle>
            <UsageDollarAmount>{pdvDollarAmount}</UsageDollarAmount>
          </UsageTitleSection>
          <ProgressSection>
            <ProgressMeter value={Math.min(100, pdv)} />
          </ProgressSection>
          <UsageAmount amount={pdvUsageDisplay ?? 0} limit={pdvLimitDisplay} />
          {!hasSubscription && (
            <UsageAlert>Upgrade to a pay-as-you-go plan to increase your usage limit for $2.50/million PDV.</UsageAlert>
          )}
        </UsageCard>
      </div>
    </div>
  )
}

export const BillingUsage = ({
  hasSubscription,
  filesUsage,
  pdvUsage,
  freeFileLimit,
  freePdvLimit,
  filesMeteredProduct,
  pdvMeteredProduct,
}: {
  hasSubscription: boolean
  filesUsage: number | undefined
  pdvUsage: number | undefined
  freeFileLimit: number
  freePdvLimit: number
  filesMeteredProduct: Product
  pdvMeteredProduct: Product
}) => {
  return (
    <BillingSection>
      <Text as='h2' className='text-2xl font-bold mb-4'>
        Usage
      </Text>
      <FilesUsage
        hasSubscription={hasSubscription}
        isLoading={filesUsage === undefined}
        {...{
          filesUsage,
          pdvUsage,
          freeFileLimit,
          freePdvLimit,
          filesMeteredProduct,
          pdvMeteredProduct,
        }}
      />
    </BillingSection>
  )
}
